import axios, {
    AxiosError,
    AxiosInstance,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL as string

const http: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 100000,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
})

const pendingRequests = new Map<string, AbortController>()

function getRequestKey(config: InternalAxiosRequestConfig): string {
    const {method, url, params, data} = config

    const stringifiedParams = params ? JSON.stringify(params) : ''
    const stringifiedData =
        typeof data === 'string' ? data : data ? JSON.stringify(data) : ''

    return `${method || 'get'}::${url || ''}::${stringifiedParams}::${stringifiedData}`
}

function removePendingRequest(config: InternalAxiosRequestConfig) {
    const requestKey = getRequestKey(config)
    pendingRequests.delete(requestKey)
}

http.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('access_token')

        if (token && config.headers) {
            config.headers.Authorization = `Bearer ${token}`
        }

        const requestKey = getRequestKey(config)

        const existingController = pendingRequests.get(requestKey)
        
        if (existingController) {
            existingController.abort()
            pendingRequests.delete(requestKey)
        }

        if (!config.signal) {
            const controller = new AbortController()
            config.signal = controller.signal
            pendingRequests.set(requestKey, controller)
        }

        return config
    },
    (error: AxiosError) => {
        return Promise.reject(error)
    }
)

http.interceptors.response.use(
    (response: AxiosResponse) => {
        removePendingRequest(response.config as InternalAxiosRequestConfig)
        return response
    },
    async (error: AxiosError) => {
        if (error.config) {
            removePendingRequest(error.config as InternalAxiosRequestConfig)
        }

        if (error.code === 'ERR_CANCELED') {
            return Promise.reject(error)
        }

        const status = error.response?.status

        if (status === 401) {
            localStorage.removeItem('access_token')
            window.location.href = '/401'
        }

        if (status === 403) {
            window.location.href = '/403'
        }

        if (status === 404) {
            window.location.href = '/404'
        }

        if (status && status >= 500) {
            window.location.href = '/error'
        }

        return Promise.reject(error)
    }
)

export {http}